import { useActiveLanguage } from '@carlito/api';
import { useBookingGuestDetails } from '@carlito/shared-ui';
import { useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FlowWithNoPresetGuestDetailsAndWithPMS } from '../shared/constants';
import { BookitoBookingFormProps, BookitoBookingFormValues } from '../shared/types';

export const useBookingFormHandler = ({
  isTimeSlotEnabled,
  shouldHideGuestDetailsForm,
  hotelId,
  event,
  formStore,
}: BookitoBookingFormProps) => {
  const {
    state: { currentStep, hasPmsEnabled, isEditMode, steps, isGuestLoggedIn },
    actions: {
      setAllParticipants,
      setCurrenStep,
      updateSuccessMsg,
      setSteps,
      setFieldError,
      setIsGuestLoggedIn,
    },
  } = formStore;

  const { values, setValues } = useFormikContext<BookitoBookingFormValues>();
  const { activeLanguage } = useActiveLanguage();
  const {
    state: { isLoadingGuest },
    actions: { loginGuest },
  } = useBookingGuestDetails();

  const goToNextStep = () => {
    setCurrenStep(steps[steps.indexOf(currentStep) + 1]);
  };

  useEffect(() => {
    if (isEditMode) {
      setSteps(['GuestExtra']);
      setCurrenStep('GuestExtra');
    } else if (!shouldHideGuestDetailsForm && hasPmsEnabled) {
      setSteps(FlowWithNoPresetGuestDetailsAndWithPMS);
    }
  }, [shouldHideGuestDetailsForm, hasPmsEnabled, isEditMode]);

  const onUpdateFormValues = (updatedValues: Partial<BookitoBookingFormValues>) => {
    setValues({
      ...values,
      ...updatedValues,
    });
  };

  const isSpaEvent = event.source === 'spa_system';

  let maxParticipants = event.maxParticipants ?? 0;
  if (isSpaEvent) {
    maxParticipants = 1;
  }

  const availableSpots = maxParticipants
    ? maxParticipants - (values.selectedTimeSlot?.numOfConfirmedParticipants ?? 0)
    : undefined;
  const potentialNewNumParticipants =
    (values.participants?.length ?? 0) + values.numOfAdditionalParticipants;

  const willBePutOnWaitingList =
    availableSpots !== undefined ? potentialNewNumParticipants > availableSpots : false;

  const maxNumberOfAdditionalParticipants =
    isTimeSlotEnabled && availableSpots
      ? (availableSpots ?? 0) - (values.participants?.length ?? 0)
      : undefined;

  const { t } = useTranslation();
  const translationState = {
    initial: {
      btnContent: t('book-now'),
      successMsg: 'the-event-was-booked-successfully',
    },
    bookable_on_request: {
      btnContent: t('send-request'),
      successMsg: 'booking-request-was-sent',
    },
    waiting_list: {
      btnContent: t('get-on-waitinglist'),
      successMsg: 'you-are-on-the-waitling-list-and-will-be-notified-as-soon-there-is-a-free-place',
    },
  };

  let bookBtnContent = translationState.initial.btnContent;

  if (event?.bookability === 'bookable_on_request') {
    bookBtnContent = translationState.bookable_on_request.btnContent;
    updateSuccessMsg?.(translationState.bookable_on_request.successMsg);
  }

  if (willBePutOnWaitingList && !isTimeSlotEnabled) {
    bookBtnContent = translationState.waiting_list.btnContent;
    updateSuccessMsg?.(translationState.waiting_list.successMsg);
  }

  if (isEditMode) bookBtnContent = t('Save');

  const resetError = () => {
    setFieldError('');
  };

  const onSubmitGuestForm = async () => {
    resetError();
    if (values?.participants?.[0]?.roomNr || values.bookingRef) {
      const { isLoggedIn, guest, error, participants } = await loginGuest({
        hotelId: hotelId ?? '',
        bookingReference: values.bookingRef,
        roomNumber: values?.participants?.[0]?.roomNr,
        birthDate: values.birthDate,
      });
      if (error) {
        setFieldError(error);
        setIsGuestLoggedIn(false);
      }
      if (isLoggedIn && guest) {
        setIsGuestLoggedIn(true);
        setAllParticipants && setAllParticipants(participants ?? []);
        onUpdateFormValues({
          participants: [
            {
              roomNr: values.participants?.[0]?.roomNr,
              isMainBooker: true,
              carlitoHubUserId: guest.id,
            },
          ],
        });
        goToNextStep();
      }
    } else {
      onUpdateFormValues({
        participants: [
          {
            name: values.participants?.[0]?.name,
            roomNr: values.participants?.[0]?.roomNr,
            phoneNr: values.participants?.[0]?.phoneNr,
            email: values.participants?.[0]?.email,
            communicationLanguage:
              values.participants?.[0]?.communicationLanguage ?? activeLanguage,
          },
        ],
      });
      goToNextStep();
    }
  };

  const currentGuest = values?.participants?.[0];

  const isBirthDateEmpty = !Boolean(values.birthDate);
  let isGuestInformationInvalidWithPMS;

  if (currentGuest?.roomNr && currentGuest?.carlitoHubUserId) {
    isGuestInformationInvalidWithPMS = false;
  } else if (currentGuest?.roomNr || values.bookingRef) {
    isGuestInformationInvalidWithPMS = isBirthDateEmpty;
  } else {
    isGuestInformationInvalidWithPMS = !currentGuest?.name;
  }

  const isGuestInformationInvalid = hasPmsEnabled
    ? isGuestInformationInvalidWithPMS
    : !currentGuest?.name && !currentGuest?.carlitoHubUserId;

  const isGuestFormStepValid =
    currentGuest?.roomNr || values.bookingRef ? isGuestLoggedIn : Boolean(currentGuest?.name);

  return {
    state: {
      values,
      isGuestFormStepValid,
      willBePutOnWaitingList,
      isTimeSlotEnabled,
      maxNumberOfAdditionalParticipants,
      bookBtnContent,
      isLoadingGuest,
      isGuestInformationInvalid,
    },
    actions: {
      resetError,
      onUpdateFormValues,
      onSubmitGuestForm,
      goToNextStep,
    },
  };
};
